import {
  IPaymentService,
  PaymentFrequency,
  PaymentMethod,
  PaymentPlanCalculation,
  PaymentRequest,
  PaymentResult,
  PaymentVerification,
  RefundResult,
} from '../../domain/services/IPaymentService';
import axios, { type AxiosInstance } from 'axios';

export interface PaymentInitializationParams {
  provider: string;
  method: string;
  phoneNumber: string;
  intent: 'checkout' | 'installment' | 'advance' | 'payoff';
  autoDebitAuthorized?: boolean;
  context?: {
    checkout_session_id?: string;
    order_id?: string;
    installment_id?: string;
    amount?: string;
  };
  idempotencyKey?: string;
}

export interface PaymentInitializationResult {
  paymentId: string;
  status: string;
  providerReference: string;
  instructions: {
    message: string;
    amount: string;
    reference: string;
    fallbackCode?: string;
  };
  appliedTo?: string[];
  scheduleEffect?: {
    installmentsCovered: number;
    nextDue: string;
    remainingBalance: string;
    planStatus: string;
  };
  pollingInterval: number;
  timeoutSeconds: number;
  /**
   * Indicates if user needs to manually approve the payment on their phone.
   * - true: User needs to approve on phone (show countdown + button)
   * - false: Auto-debit will happen (show simple spinner)
   * - undefined: Backend doesn't provide this (use fallback heuristic)
   */
  requiresUserApproval?: boolean;
}

export interface PaymentStatusResult {
  paymentId: string;
  checkoutSessionId?: string;
  status: string;
  providerStatus: string;
  amount: number;
  completedAt?: string;
  providerReference: string;
  receiptUrl?: string;
}

export interface PaymentStatusPollResult {
  paymentId: string;
  paymentReference: string;
  status: string;
  statusDisplay: string;
  provider: string;
  amount: number;
  currency: string;
  providerReference: string;
  nextPollAfter: string;
  requiresAction: boolean;
}

export class ApiPaymentService implements IPaymentService {
  private readonly apiClient:AxiosInstance;

  constructor(private readonly baseUrl: string) {
    this.baseUrl = baseUrl;
    this.apiClient = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  async initializePayment(
    params: PaymentInitializationParams
  ): Promise<PaymentInitializationResult> {
    const response = await this.apiClient.post(
      '/v1/payments/initialize',
      {
        intent: params.intent || 'checkout',
        context: params.context,
        payment_provider: params.provider,
        payment_method: params.method,
        phone_number: params.phoneNumber,
        idempotency_key: params.idempotencyKey || this.generateIdempotencyKey(),
        auto_debit_authorized: params.autoDebitAuthorized ?? true,
      },
      {
        // headers: { Authorization: `Bearer ${this.getAuthToken()}` },
      }
    );

    return {
      paymentId: response.data.payment_id,
      status: response.data.status,
      providerReference: response.data.provider_reference,
      instructions: {
        message: response.data.instructions.message,
        amount: response.data.instructions.amount,
        reference: response.data.instructions.reference,
        fallbackCode: response.data.instructions.fallback_code,
      },
      appliedTo: response.data.applied_to,
      scheduleEffect: response.data.schedule_effect,
      pollingInterval: response.data.polling_interval || 5,
      timeoutSeconds: response.data.timeout_seconds || 300,
      requiresUserApproval: response.data.requires_user_approval,
    };
  }

  async getPaymentStatus(paymentId: string): Promise<PaymentStatusResult> {
    // NOTE: Staging currently requires a trailing slash for this endpoint; keep it
    // until backend is deployed with slashless support.
    const response = await this.apiClient.get(`/v1/payments/${paymentId}/`, {
      // headers: { Authorization: `Bearer ${this.getAuthToken()}` },
    });

    return {
      paymentId: response.data.payment_id,
      checkoutSessionId: response.data.checkout_session_id,
      status: response.data.status,
      providerStatus: response.data.provider_status,
      amount: parseFloat(response.data.amount),
      completedAt: response.data.completed_at,
      providerReference: response.data.provider_reference,
      receiptUrl: response.data.receipt_url,
    };
  }

  async getPaymentStatusPoll(identifier: string): Promise<PaymentStatusPollResult> {
    const response = await this.apiClient.get(`/v1/payments/${identifier}/status`, {
      // headers: { Authorization: `Bearer ${this.getAuthToken()}` },
    });

    return {
      paymentId: response.data.payment_id,
      paymentReference: response.data.payment_reference,
      status: response.data.status,
      statusDisplay: response.data.status_display,
      provider: response.data.provider,
      amount: typeof response.data.amount === 'number' ? response.data.amount : parseFloat(response.data.amount),
      currency: response.data.currency,
      providerReference: response.data.provider_reference,
      nextPollAfter: response.data.next_poll_after,
      requiresAction: response.data.requires_action,
    };
  }

  async getUpcomingPayments(_userId: string, limit: number = 20): Promise<UpcomingPaymentsResponse> {
    const response = await this.apiClient.get(
      '/v1/users/me/payments/upcoming',
      {
        params: { limit },
        // headers: { Authorization: `Bearer ${this.getAuthToken()}` },
      }
    );
    return response.data;
  }

  calculatePaymentPlan(
    _amount: number,
    _frequency: PaymentFrequency,
    _duration: number
  ): PaymentPlanCalculation {
    return {} as any;
  }

  getPaymentMethods(): Promise<PaymentMethod[]> {
    return Promise.resolve([]);
  }

  processPayment(_paymentRequest: PaymentRequest): Promise<PaymentResult> {
    return Promise.resolve({} as any);
  }

  refundPayment(_transactionId: string, _amount?: number): Promise<RefundResult> {
    return Promise.resolve({} as any);
  }

  verifyPayment(_transactionId: string): Promise<PaymentVerification> {
    return Promise.resolve({} as any);
  }

  async getPaymentPlanRecommendations(_request: any) {}

  async calculatePSSPayment(_request: any) {}

  private generateIdempotencyKey(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private getAuthToken(): string {
    // In a real implementation, this would get the token from your auth store
    return '';
  }
}


export interface UpcomingPaymentsResponse {
  results: UpcomingPayment[];
  summary: UpcomingPaymentsSummary;
  payment_calendar: UpcomingPaymentCalendarEntry[];
}

export interface UpcomingPaymentCalendarEntry {
  date: string;
  payments: UpcomingPayment[];
}

export interface UpcomingPayment {
  id: string;
  order_id: string;
  order_number: string;
  product_name: string;
  amount: string;
  currency: string;
  due_date: string;
  status: string;
  payment_number: number;
  total_payments: number;
  payment_method: string;
  auto_debit: boolean;
  days_until_due: number;
  late_fee: string;
  can_pay_early: boolean;
  early_payment_discount: string;
}

export interface UpcomingPaymentsSummary {
  total_upcoming: number;
  total_amount: string;
  due_this_week: number;
  due_this_month: number;
  overdue_count: number;
  auto_debit_enabled: number;
}

// Checkout Session Service
export interface CheckoutSessionParams {
  productId: string;
  quantity: number;
  paymentMethod?: string;
  paymentOption: string;
  paymentPlan?: {
    durationMonths: number;
    frequency: string;
    kmpEnabled: boolean;
  };
  deliveryOption?: string;
  deliveryAddressId?: string;
}

export interface CheckoutSessionResult {
  sessionId: string;
  orderId: string;
  orderNumber: string;
  status: string;
  paymentBreakdown: {
    productTotal: string;
    kmpFee: string;
    processingFee: string;
    deliveryFee: string;
    totalAmount: string;
    currency: string;
    amountDueNow?: string;
    remainingBalance?: string;
    monthlyContribution?: string;
    monthlyKmpFee?: string;
  };
  selectedPlan?: any;
  paymentSchedule?: any[];
  nextStep: string;
  completionDate: string;
  expiresAt: string;
  transactionId?: string;
}

export interface CurrentCheckoutSession {
  id: string;
  session_id: string;
  account_id?: string;
  order_number: string;
  status: string;
  expires_at: string | null;
  created_at?: string;
  updated_at?: string;
  product?: Product;
  items?: any[];
  payment_breakdown: PaymentBreakdown & { requires_delivery_fee_invoice?: boolean };
  selected_plan?: any;
  payment_schedule?: any[];
  available_payment_methods: AvailablePaymentMethod[];
  auto_debit: AutoDebit & { next_execution_at?: string };
  delivery?: {
    delivery_option?: string | null;
    delivery_address_id?: string | null;
    delivery_note?: string | null;
    pickup_note?: string | null;
    delivery_preferences?: Record<string, unknown>;
  };
  next_step?: string;
  completion_date?: string | null;
}

export interface AutoDebit {
  available: boolean;
  enabled: boolean;
}

export interface AvailablePaymentMethod {
  type: string;
  provider?: string | null;
  display_name?: string; // legacy/mock
  available?: boolean;
  is_default: boolean;
  requires_phone: boolean;
  auto_debit_available: boolean;
}

export interface PaymentBreakdown {
  product_total: string;
  amount_due_now: string;
  remaining_balance: string;
  currency: string;
  kmp_fee?: string;
  processing_fee?: string;
  delivery_fee?: string;
  total_amount?: string;
  monthly_contribution?: string; // legacy/mock
  monthly_kmp_fee?: string; // legacy/mock
}

export interface PaymentPlan {
  payment_method: string;
  duration_months: number;
  frequency: string;
  kmp_enabled: boolean;
  pss_provider: string;
}

export interface Product {
  name: string;
  price: string;
  quantity: number;
  image_url: string;
  vendor: string;
  shop_logo?: string;
  shop_name?: string;
}

export class CheckoutSessionService {
  private readonly baseUrl: string;
  private readonly apiClient;

  constructor(
    baseUrl: string = process.env.NEXT_PUBLIC_API_BASE_URL ||
      'https://api-staging.motito.co'
  ) {
    this.baseUrl = baseUrl;
    this.apiClient = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  async createSession(
    params: CheckoutSessionParams
  ): Promise<CheckoutSessionResult> {
    const response = await this.apiClient.post(
      '/v1/checkout-sessions',
      {
        product_id: params.productId,
        quantity: params.quantity,
        payment_option: params.paymentOption,
        payment_plan: params.paymentPlan
          ? {
              duration_months: params.paymentPlan.durationMonths,
              frequency: params.paymentPlan.frequency,
              kmp_enabled: params.paymentPlan.kmpEnabled,
            }
          : undefined,
        method: params.deliveryOption,
        delivery_address_id: params.deliveryAddressId,
      },
    );

    return this.mapCheckoutSessionResponse(response.data);
  }

  async getSession(sessionId: string): Promise<CurrentCheckoutSession> {
    const response = await this.apiClient.get(
      `/v1/checkout-sessions/${sessionId}`
    );
 
    return response.data;
  }

  async updateSession(
    sessionId: string,
    updates: any
  ): Promise<CheckoutSessionResult> {
    const response = await this.apiClient.patch(
      `/v1/checkout-sessions/${sessionId}`,
      updates
    );

    return this.mapCheckoutSessionResponse(response.data);
  }

  async createOrderFromCheckoutSession(payload: {
    checkoutSessionId: string;
    paymentMethod: 'direct_payment' | 'paysmallsmall';
    deliveryMethod?: 'HOME_DELIVERY' | 'COLLECTION_POINT';
    deliveryAddressId?: string | null;
    deliveryPreferences?: Record<string, unknown>;
    deliveryNote?: string | null;
    pickupNote?: string | null;
    deliveryPhone?: string | null;
  }): Promise<any> {
    const response = await this.apiClient.post('/v1/orders', {
      checkout_session_id: payload.checkoutSessionId,
      payment_method: payload.paymentMethod,
      delivery_method: payload.deliveryMethod,
      delivery_address_id: payload.deliveryAddressId,
      delivery_preferences: payload.deliveryPreferences,
      delivery_note: payload.deliveryNote,
      pickup_note: payload.pickupNote,
      delivery_phone: payload.deliveryPhone,
    });

    return response.data;
  }

  async listCollectionPoints(params: {
    region: string;
    search?: string;
    pointType?: string;
    active?: boolean;
  }): Promise<Array<{
    id: string;
    code: string;
    name: string;
    region: string;
    city: string;
    point_type: string;
    status: string;
    operating_hours: Record<string, unknown>;
    is_active: boolean;
    metadata: Record<string, unknown>;
  }>> {
    const response = await this.apiClient.get('/v1/collection-points', {
      params: {
        region: params.region,
        search: params.search,
        point_type: params.pointType,
        active: typeof params.active === 'boolean' ? String(params.active) : undefined,
      },
    });

    const payload = response.data;
    if (Array.isArray(payload)) {
      return payload;
    }
    if (payload && Array.isArray(payload.results)) {
      return payload.results;
    }
    return [];
  }

  private mapCheckoutSessionResponse(data: any): CheckoutSessionResult {
    return {
      sessionId: data.session_id,
      orderId: data.order_id ?? data.session_id,
      orderNumber: data.order_number,
      status: data.status,
      paymentBreakdown: {
        productTotal: data.payment_breakdown?.product_total || '0',
        kmpFee: data.payment_breakdown?.kmp_fee || '0',
        processingFee: data.payment_breakdown?.processing_fee || '0',
        deliveryFee: data.payment_breakdown?.delivery_fee || '0',
        totalAmount: data.payment_breakdown?.total_amount || '0',
        currency: data.payment_breakdown?.currency || 'GHS',
        amountDueNow: data.payment_breakdown?.amount_due_now,
        remainingBalance: data.payment_breakdown?.remaining_balance,
        monthlyContribution: data.payment_breakdown?.monthly_contribution,
        monthlyKmpFee: data.payment_breakdown?.monthly_kmp_fee,
      },
      selectedPlan: data.selected_plan,
      paymentSchedule: data.payment_schedule,
      nextStep: data.next_step,
      completionDate: data.completion_date,
      expiresAt: data.expires_at,
      transactionId: data.transaction_id,
    };
  }

  private getAuthToken(): string {
    return '';
  }
}

// PSS Calculation Service
export interface PSSPlansResponse {
  product: any;
  kmpPolicy: {
    available: boolean;
    basePercentage: number;
    minFee: string;
    currency: string;
  };
  userEligibility: {
    repScore: number;
    pssCreditEligible: boolean;
    pssCreditPercentage: number;
    earlyRedemptionThreshold: number;
    recommendedDuration: number;
    maxFinanceableAmount: string;
  };
  durationOptions: any[];
}

export class PSSCalculationService {
  private readonly baseUrl: string;
  private readonly apiClient;

  constructor(
    baseUrl: string = process.env.NEXT_PUBLIC_API_BASE_URL ||
      'https://api-staging.motito.co'
  ) {
    this.baseUrl = baseUrl;
    this.apiClient = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  async getPaymentPlans(productId: string): Promise<PSSPlansResponse> {
    const response = await this.apiClient.get(`/v1/products/${productId}/payment-plans`);

    return {
      product: response.data.product,
      kmpPolicy: response.data.kmp_policy,
      userEligibility: response.data.user_eligibility,
      durationOptions: response.data.duration_options,
    };
  }

  private getAuthToken(): string {
    return '';
  }
}
